import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  SortingState,
  getSortedRowModel,
  ColumnFiltersState,
  getFilteredRowModel,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationControl } from "@/components/ui/table-items/PaginationControl";
import { useState } from "react";
import { StateMessageFactory } from "@/components/shared/state-messages/StateMessageFactory";
import { SearchSurnames } from "../../invites/table-items/SearchSurnames";
import { ConditionsFilter } from "../table-items/ConditionsFilter";
import { EDigestiveConditions } from "@/app/types/enumerations";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}

export function PatientsTable<TData, TValue>({
  columns,
  data,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
    },
  });

  if (data.length === 0) {
    return (
      <div className="mt-12 2xl:mt-24 mx-auto xl:max-w-4/5 2xl:max-w-3/5">
        {StateMessageFactory("no-patients-yet")}
      </div>
    );
  }

  return (
    <>
      <div className="flex items-end justify-between py-4">
        <SearchSurnames
          value={
            (table.getColumn("surnames")?.getFilterValue() as string) ?? ""
          }
          onChange={(event) =>
            table.getColumn("surnames")?.setFilterValue(event.target?.value)
          }
        />
        <ConditionsFilter
          onValueChange={(value: EDigestiveConditions) =>
            value !== EDigestiveConditions.NULL
              ? table.getColumn("condition")?.setFilterValue(value)
              : table.getColumn("condition")?.setFilterValue([])
          }
        />
      </div>

      <div className="rounded-md border bg-white">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="flex mx-auto py-12 max-w-5/6">
                    {StateMessageFactory("no-search-results")}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationControl
        preOnClick={() => table.previousPage()}
        preDisabled={!table.getCanPreviousPage()}
        nextOnClick={() => table.nextPage()}
        nextDisabled={!table.getCanNextPage()}
      />
    </>
  );
}
